/**
 * WebGPU 后端实现（DESIGN.md §2.8）。
 *
 * **这是 src 下唯一允许出现 `GPUDevice` / `navigator.gpu` 的地方。**
 * 其余代码一律通过 {@link ../backend} 的抽象访问 GPU，否则回退到 WebGL2 的路径
 * 就会在某个角落悄悄失效。test/gpu/layering.test.ts 用 grep 持续守这条线。
 */
import {
  BYTES_PER_PIXEL,
  DestroyedResourceError,
  validateTextureDescriptor,
  type BackendCapabilities,
  type BatchDescriptor,
  type DrawDescriptor,
  type GpuBackend,
  type GpuProgram,
  type GpuTexture,
  type ProgramDescriptor,
  type ReadbackResult,
  type TextureDescriptor,
  type TextureFormat,
  type UniformValue,
} from '../backend';

/** 抽象格式 → WebGPU 格式。一一对应，没有需要模拟的情况。 */
const WEBGPU_FORMAT: Readonly<Record<TextureFormat, GPUTextureFormat>> = {
  rgba8unorm: 'rgba8unorm',
  rgba16float: 'rgba16float',
  r8unorm: 'r8unorm',
  r16float: 'r16float',
};

/** WebGPU 要求 copyTextureToBuffer 的 bytesPerRow 是 256 的倍数。 */
const BYTES_PER_ROW_ALIGNMENT = 256;

function alignUp(value: number, alignment: number): number {
  return Math.ceil(value / alignment) * alignment;
}

/**
 * 半精度浮点转 f32。
 *
 * JS 没有原生 f16 读取（`DataView` 无 `getFloat16`），而 `rgba16float` 是合成
 * 链路的主力格式，readback 必须能解。按 IEEE 754 binary16 手工解码，
 * 包含次正规数与 Inf/NaN——像素对拍时 NaN 与 0 的区别至关重要。
 */
export function float16ToFloat32(bits: number): number {
  const sign = (bits & 0x8000) !== 0 ? -1 : 1;
  const exponent = (bits >> 10) & 0x1f;
  const mantissa = bits & 0x03ff;

  if (exponent === 0) {
    // 次正规数：没有隐含的前导 1
    return sign * mantissa * 2 ** -24;
  }
  if (exponent === 0x1f) {
    return mantissa === 0 ? sign * Infinity : Number.NaN;
  }
  return sign * (1 + mantissa / 1024) * 2 ** (exponent - 15);
}

class WebGpuTexture implements GpuTexture {
  #destroyed = false;

  constructor(
    readonly texture: GPUTexture,
    readonly width: number,
    readonly height: number,
    readonly format: TextureFormat,
  ) {}

  get destroyed(): boolean {
    return this.#destroyed;
  }

  /** 取原生纹理，已释放则抛错而不是返回悬垂句柄。 */
  native(): GPUTexture {
    if (this.#destroyed) throw new DestroyedResourceError('纹理');
    return this.texture;
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.texture.destroy();
  }
}

class WebGpuProgram implements GpuProgram {
  #destroyed = false;

  constructor(
    readonly pipeline: GPURenderPipeline,
    readonly module: GPUShaderModule,
  ) {}

  get destroyed(): boolean {
    return this.#destroyed;
  }

  native(): GPURenderPipeline {
    if (this.#destroyed) throw new DestroyedResourceError('着色器程序');
    return this.pipeline;
  }

  destroy(): void {
    // WebGPU 的管线与模块由 GC 回收，没有显式 destroy；标记状态是为了
    // 让"使用已释放资源"在两个后端上有一致的失败方式。
    this.#destroyed = true;
  }
}

/** 把 uniform 打包成一个 std140 兼容的 f32 缓冲，纹理另行绑定。 */
interface PackedUniforms {
  /** 显式带 ArrayBuffer 参数：TS 5.9 起不注解会推断出 ArrayBufferLike，与 WebGPU 的
   *  GPUAllowSharedBufferSource 不相容。 */
  readonly scalars: Float32Array<ArrayBuffer>;
  readonly textures: readonly WebGpuTexture[];
}

function packUniforms(uniforms: Readonly<Record<string, UniformValue>>): PackedUniforms {
  const scalars: number[] = [];
  const textures: WebGpuTexture[] = [];

  // 按名字排序，保证 JS 对象字面量顺序变化不会悄悄改变绑定布局。
  for (const key of Object.keys(uniforms).sort()) {
    const value = uniforms[key];
    if (typeof value === 'number') scalars.push(value);
    else if (Array.isArray(value)) scalars.push(...(value as readonly number[]));
    else if (value instanceof WebGpuTexture) textures.push(value);
  }
  // uniform buffer 至少 16 字节且按 16 字节对齐
  while (scalars.length === 0 || scalars.length % 4 !== 0) scalars.push(0);
  return { scalars: Float32Array.from(scalars), textures };
}

class WebGpuBackend implements GpuBackend {
  readonly kind = 'webgpu' as const;
  readonly capabilities: BackendCapabilities;
  #destroyed = false;

  constructor(
    private readonly device: GPUDevice,
    private readonly onLost: Promise<GPUDeviceLostInfo>,
  ) {
    this.capabilities = {
      // WebGPU 规范要求 rgba16float 可渲染，无需扩展探测。
      renderToFloat16: true,
      maxTextureSize: device.limits.maxTextureDimension2D,
    };
    void this.onLost;
  }

  #assertAlive(): void {
    if (this.#destroyed) throw new DestroyedResourceError('GPU 后端');
  }

  createTexture(descriptor: TextureDescriptor): GpuTexture {
    this.#assertAlive();
    validateTextureDescriptor(descriptor, this.capabilities.maxTextureSize);

    const usage =
      GPUTextureUsage.TEXTURE_BINDING |
      GPUTextureUsage.COPY_SRC |
      GPUTextureUsage.COPY_DST |
      (descriptor.renderTarget === true ? GPUTextureUsage.RENDER_ATTACHMENT : 0);

    const texture = this.device.createTexture({
      size: { width: descriptor.width, height: descriptor.height },
      format: WEBGPU_FORMAT[descriptor.format],
      usage,
      ...(descriptor.label === undefined ? {} : { label: descriptor.label }),
    });

    return new WebGpuTexture(texture, descriptor.width, descriptor.height, descriptor.format);
  }

  writeTexture(texture: GpuTexture, data: Uint8Array): void {
    this.#assertAlive();
    if (!(texture instanceof WebGpuTexture)) {
      throw new TypeError('writeTexture 收到的不是本后端创建的纹理。');
    }
    if (texture.format !== 'rgba8unorm' && texture.format !== 'r8unorm') {
      throw new RangeError(`writeTexture 目前只支持 8 位格式，收到 ${texture.format}。`);
    }
    const bpp = BYTES_PER_PIXEL[texture.format];
    if (data.length !== texture.width * texture.height * bpp) {
      throw new RangeError(
        `writeTexture 数据长度 ${data.length} 与 ${texture.width}×${texture.height}(${texture.format}) 不符。`,
      );
    }
    // queue.writeTexture 不要求 256 字节行对齐（那是 copyBufferToTexture 的限制）
    this.device.queue.writeTexture(
      { texture: texture.native() },
      data as BufferSource,
      { bytesPerRow: texture.width * bpp },
      { width: texture.width, height: texture.height },
    );
  }

  createProgram(descriptor: ProgramDescriptor): GpuProgram {
    this.#assertAlive();
    const module = this.device.createShaderModule({
      code: descriptor.source.wgsl,
      ...(descriptor.label === undefined ? {} : { label: descriptor.label }),
    });

    const pipeline = this.device.createRenderPipeline({
      layout: 'auto',
      vertex: { module, entryPoint: 'vs_main' },
      fragment: {
        module,
        entryPoint: 'fs_main',
        targets: [{ format: WEBGPU_FORMAT[descriptor.targetFormat] }],
      },
      primitive: { topology: 'triangle-list' },
      ...(descriptor.label === undefined ? {} : { label: descriptor.label }),
    });

    return new WebGpuProgram(pipeline, module);
  }

  draw(descriptor: DrawDescriptor): void {
    this.#assertAlive();
    const { program, target, vertexCount, uniforms, clear } = descriptor;

    if (!(target instanceof WebGpuTexture)) {
      throw new TypeError('渲染目标必须由同一个 WebGPU 后端创建。');
    }
    if (!(program instanceof WebGpuProgram)) {
      throw new TypeError('着色器程序必须由同一个 WebGPU 后端创建。');
    }

    const pipeline = program.native();
    const encoder = this.device.createCommandEncoder();
    const pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view: target.native().createView(),
          loadOp: clear === undefined ? 'load' : 'clear',
          storeOp: 'store',
          ...(clear === undefined
            ? {}
            : { clearValue: { r: clear[0], g: clear[1], b: clear[2], a: clear[3] } }),
        },
      ],
    });

    pass.setPipeline(pipeline);

    // 只在调用方真的传了 uniform 时才建绑定组。
    //
    // 这里**绝不能**用 `try { pipeline.getBindGroupLayout(0) } catch` 去探测管线有没有
    // 绑定：对 `layout:'auto'` 且无绑定的管线，该调用除了抛 RangeError，还会向 device
    // 提交一个 validation error，进而让**当前 command encoder 整个失效**——
    // finish() 得到的 command buffer 被丢弃，submit 静默无效，连 clear 都不会发生。
    // 症状是渲染结果全 0 而没有任何异常，极难排查（M0-04 实际踩过）。
    if (uniforms !== undefined && Object.keys(uniforms).length > 0) {
      const packed = packUniforms(uniforms);
      const buffer = this.device.createBuffer({
        size: packed.scalars.byteLength,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      });
      this.device.queue.writeBuffer(buffer, 0, packed.scalars);

      const entries: GPUBindGroupEntry[] = [{ binding: 0, resource: { buffer } }];
      packed.textures.forEach((tex, i) => {
        entries.push({ binding: i + 1, resource: tex.native().createView() });
      });
      pass.setBindGroup(0, this.device.createBindGroup({
        layout: pipeline.getBindGroupLayout(0),
        entries,
      }));
    }

    pass.draw(vertexCount);
    pass.end();
    this.device.queue.submit([encoder.finish()]);
  }

  drawBatch(descriptor: BatchDescriptor): void {
    this.#assertAlive();
    const { target, items, clear } = descriptor;
    if (!(target instanceof WebGpuTexture)) {
      throw new TypeError('渲染目标必须由同一个 WebGPU 后端创建。');
    }
    // 不能因为 items 空就整个早退——那样 `clear` 也一起被跳掉了，target
    // 留着上一次渲染（纹理池复用回来的）的旧内容。一个 tile 的所有图层
    // 全部隐藏时 items 恰好是空数组，这条早退会让"隐藏后再显示"这类
    // 操作看起来卡在隐藏前那一帧——下面照常开一个 render pass，循环体
    // 对空数组自然不做任何 draw，但 `clear` 依然会执行。

    // 整批共用一个 encoder 与一个 render pass——这正是批量存在的意义，
    // 逐次 draw 的固定开销约 0.26 ms，几百个 tile 就吃满一帧预算。
    const encoder = this.device.createCommandEncoder({ label: 'batch' });
    const pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view: target.native().createView(),
          loadOp: clear === undefined ? 'load' : 'clear',
          storeOp: 'store',
          ...(clear === undefined
            ? {}
            : { clearValue: { r: clear[0], g: clear[1], b: clear[2], a: clear[3] } }),
        },
      ],
    });

    for (const item of items) {
      if (!(item.program instanceof WebGpuProgram)) {
        throw new TypeError('着色器程序必须由同一个 WebGPU 后端创建。');
      }
      pass.setPipeline(item.program.native());

      if (item.viewport !== undefined) {
        const [x, y, w, h] = item.viewport;
        pass.setViewport(x, y, w, h, 0, 1);
      }

      if (item.uniforms !== undefined && Object.keys(item.uniforms).length > 0) {
        pass.setBindGroup(0, this.#bindGroupFor(item.program.native(), item.uniforms));
      }
      pass.draw(item.vertexCount);
    }

    pass.end();
    this.device.queue.submit([encoder.finish()]);
  }

  /**
   * 取或建 uniform 绑定组。
   *
   * 按「管线 + uniform 取值」缓存：分块合成里，同一层的 uniform 在整帧中完全相同，
   * 每个 tile 重建一次 buffer + bind group 是纯粹的浪费（实测占单次 draw 开销的三分之二）。
   */
  #bindGroupFor(
    pipeline: GPURenderPipeline,
    uniforms: Readonly<Record<string, UniformValue>>,
  ): GPUBindGroup {
    const packed = packUniforms(uniforms);
    const key = `${this.#pipelineId(pipeline)}|${packed.scalars.join(',')}|${packed.textures.length}`;
    const cached = this.#bindGroupCache.get(key);
    if (cached !== undefined && packed.textures.length === 0) return cached;

    const buffer = this.device.createBuffer({
      size: packed.scalars.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(buffer, 0, packed.scalars);

    const entries: GPUBindGroupEntry[] = [{ binding: 0, resource: { buffer } }];
    packed.textures.forEach((tex, i) => {
      entries.push({ binding: i + 1, resource: tex.native().createView() });
    });
    const group = this.device.createBindGroup({
      layout: pipeline.getBindGroupLayout(0),
      entries,
    });

    // 只缓存不含纹理的组合：纹理视图的生命周期跟着纹理走，缓存住会拖住已销毁的资源。
    if (packed.textures.length === 0) this.#bindGroupCache.set(key, group);
    return group;
  }

  #pipelineIds = new WeakMap<GPURenderPipeline, number>();
  #nextPipelineId = 1;
  #bindGroupCache = new Map<string, GPUBindGroup>();

  #pipelineId(pipeline: GPURenderPipeline): number {
    let id = this.#pipelineIds.get(pipeline);
    if (id === undefined) {
      id = this.#nextPipelineId++;
      this.#pipelineIds.set(pipeline, id);
    }
    return id;
  }

  async readback(texture: GpuTexture, signal?: AbortSignal): Promise<ReadbackResult> {
    this.#assertAlive();
    // 包成函数而非直接比较：readback 中途会 await，取消可能发生在等待期间，
    // 而直接写两次 `signal?.aborted === true` 会被 TS 收窄成永假。
    const aborted = (): boolean => signal?.aborted === true;
    if (aborted()) throw new Error('readback 已取消。');
    if (!(texture instanceof WebGpuTexture)) {
      throw new TypeError('纹理必须由同一个 WebGPU 后端创建。');
    }

    const bpp = BYTES_PER_PIXEL[texture.format];
    const unpaddedBytesPerRow = texture.width * bpp;
    const paddedBytesPerRow = alignUp(unpaddedBytesPerRow, BYTES_PER_ROW_ALIGNMENT);

    const buffer = this.device.createBuffer({
      size: paddedBytesPerRow * texture.height,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    });

    const encoder = this.device.createCommandEncoder();
    encoder.copyTextureToBuffer(
      { texture: texture.native() },
      { buffer, bytesPerRow: paddedBytesPerRow },
      { width: texture.width, height: texture.height },
    );
    this.device.queue.submit([encoder.finish()]);

    try {
      await buffer.mapAsync(GPUMapMode.READ);
      if (aborted()) throw new Error('readback 已取消。');

      const padded = new Uint8Array(buffer.getMappedRange());
      // 去掉每行的对齐填充，还原成紧密排列
      const tight = new Uint8Array(unpaddedBytesPerRow * texture.height);
      for (let y = 0; y < texture.height; y++) {
        tight.set(
          padded.subarray(y * paddedBytesPerRow, y * paddedBytesPerRow + unpaddedBytesPerRow),
          y * unpaddedBytesPerRow,
        );
      }
      return decodeToResult(tight, texture.format);
    } finally {
      buffer.unmap();
      buffer.destroy();
    }
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.device.destroy();
  }
}

/** 按格式把原始字节解码成断言友好的类型。 */
function decodeToResult(bytes: Uint8Array, format: TextureFormat): ReadbackResult {
  if (format === 'rgba8unorm' || format === 'r8unorm') return bytes;

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const out = new Float32Array(bytes.byteLength / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = float16ToFloat32(view.getUint16(i * 2, true));
  }
  return out;
}

/**
 * 创建 WebGPU 后端。
 *
 * **不可用时返回 `null`，绝不抛异常**——"这台设备没有 WebGPU"是预期内的正常分支，
 * 用异常表达会让调用方被迫 try/catch 一个非错误的情况，也容易把真正的错误吞掉。
 * 真正的异常（如 device 创建过程中的内部错误）同样被吸收为 `null` 并 warn。
 */
export async function createWebGPUBackend(): Promise<GpuBackend | null> {
  const gpu = (globalThis.navigator as Navigator | undefined)?.gpu;
  if (gpu === undefined) return null;

  try {
    const adapter = await gpu.requestAdapter({ powerPreference: 'high-performance' });
    if (adapter === null) return null;

    const device = await adapter.requestDevice();
    return new WebGpuBackend(device, device.lost);
  } catch (error) {
    console.warn('[gpu] WebGPU 初始化失败，将回退到 WebGL2。', error);
    return null;
  }
}
